/* import __COLOCATED_TEMPLATE__ from './static-report.hbs'; */
/* RESPONSIBLE TEAM: team-reporting */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
// @ts-ignore
import { copy } from 'ember-copy';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import Range from 'embercom/models/reporting/range';
import { flatten, groupBy } from 'underscore';
import {
  CUSTOM_COMPANY_ATTRIBUTE,
  CUSTOM_USER_ATTRIBUTE,
} from 'embercom/objects/reporting/unified/datasets/types';
import { modifier } from 'ember-modifier';
import type Store from '@ember-data/store';
import type ReportingMetrics from 'embercom/services/reporting-metrics';
import type { MetricPropertyDefinition } from 'embercom/objects/reporting/unified/properties/types';
import type {
  TopLevelFilter,
  LogicalFilterOperator,
} from 'embercom/components/reporting/custom/filters';
import type { StickyFilter } from 'embercom/components/reporting/custom/chart-builder/filter-bar/filter-bar';
import ConversationAttributeDescriptor from 'embercom/models/conversation-attributes/descriptor';
import type ReportingService from 'embercom/services/reporting-service';
import type ReportingAgentSidebarService from 'embercom/services/reporting-agent-sidebar';
import { captureException } from 'embercom/lib/sentry';
import type { ReportState } from 'embercom/components/reporting/custom/report/chart-card';
import { use } from 'ember-resources/util/function-resource';
import QueryParams from 'embercom/resources/utils/query-params';
import type { DrillInViewType } from 'embercom/components/reporting/custom/report/underlying-data-modal';

export interface SerializedChartSeries {
  metric_id: string;
  aggregation?: string;
  filters?: any;
  numerator_filters?: any;
  color?: string;
}

export interface SerializedChart {
  id?: string;
  chart_series: SerializedChartSeries[];
  filters?: any;
}

interface Signature {
  Args: {
    reportDefinition: {
      id: string;
      charts?: SerializedChart[];
      filters?: TopLevelFilter;
      date_range?: any;
      timezone?: string;
    };
    datasetId: string;
    settings?: any;
    renderTimezones?: boolean;
    persistFilters?: boolean;
    useSharedDateRange?: boolean;
    filterableProperties: string[];
    dashboard?: string;
    customFilterArgs?: Record<string, any>;
    conversationAttributeDescriptors?: ConversationAttributeDescriptor[];
    loadKnownValuesSources?: string[];
    stickyFilterTypes?: StickyFilter[];
    showNewHeader?: boolean;
    isAddFilterDisabled?: boolean;
    isDisabled?: boolean;
    drillInViews?: DrillInViewType[];
    shouldShowRestrictedViewTooltip?: boolean;
    restrictedViewLearnMoreUrl?: string;
  };
  Blocks: {
    default: any;
    afterHeading?: any;
    header?: any;
    heading?: any;
  };
  Element: HTMLElement;
}

export default class StaticReport extends Component<Signature> {
  @service declare store: Store;
  @service declare appService: AppService;
  @service declare reportingService: ReportingService;
  @service declare intercomEventService: any;
  @service declare reportingMetrics: ReportingMetrics;
  @service declare reportingAgentSidebar: ReportingAgentSidebarService;

  @use queryParams = QueryParams(this);

  reportId = this.args.reportDefinition['id'];

  constructor(owner: any, args: Signature['Args']) {
    super(owner, args);
    if (!this.args.reportDefinition) {
      throw Error('Missing value for @reportDefinition');
    }

    if (!this.args.datasetId) {
      throw Error('Missing value for @datasetId');
    }

    let reportDefinition = this.sanitizedReportDefinition;
    let persistedFilters = this.safelyGetFiltersFromLocalStorage();
    if (persistedFilters) {
      reportDefinition.filters = persistedFilters;
    }

    if (this.args.renderTimezones) {
      reportDefinition.timezone = this.defaultTimezone;
    }

    this.store.pushPayload({
      'reporting/custom/report': reportDefinition,
    });
  }

  onFiltersChange = modifier(
    (_, [filters]) => {
      if (this.args.persistFilters) {
        this.saveFiltersToLocalStorage(filters as TopLevelFilter);
      }
    },
    { eager: false },
  );

  @action
  addChartsToReport(serializedCharts: SerializedChart[]) {
    let charts = [];
    for (let serializedChart of serializedCharts) {
      let chart = this.addChartToReport(serializedChart);
      charts.push(chart);
    }
    return charts;
  }

  addChartToReport(serializedChart: SerializedChart) {
    let index = this.report.charts.length;
    serializedChart = copy(serializedChart, true);
    serializedChart.id = `chart-${this.reportId}-${index}`;
    this.store.pushPayload({ 'reporting/custom/chart': serializedChart });
    let chart = this.store.peekRecord('reporting/custom/chart', serializedChart.id);
    this.report.charts.pushObject(chart);
    return chart;
  }

  get sanitizedReportDefinition() {
    let sanitizedReportDefinition = copy(this.args.reportDefinition, true);
    sanitizedReportDefinition['is_static_report'] = true;
    // Sanitize ids so we don't accidentally overwrite other custom reports in the store
    sanitizedReportDefinition['id'] = this.reportId;
    sanitizedReportDefinition.charts = []; // these will be added when rendering the report sections
    return sanitizedReportDefinition;
  }

  willDestroy() {
    super.willDestroy();
    if (this.report) {
      // Remove all chart records
      this.report.charts.toArray().forEach((chart: any) => {
        chart.unloadRecord();
      });
      // Remove the report record
      this.report.unloadRecord();
    }
  }

  get conversationAttributeDescriptors() {
    return (
      this.args.conversationAttributeDescriptors ||
      ConversationAttributeDescriptor.peekAllAndMaybeLoad()
    );
  }

  get report() {
    return this.store.peekRecord('reporting/custom/report', this.reportId);
  }

  get reportState(): ReportState {
    return {
      settings: this.args.settings,
      dateRange: this.range,
      filters: this.report.filters,
      isStatic: true,
      timezone: this.timezone,
      id: this.reportId,
    };
  }

  get filtersWithDateRange() {
    return this.report.filters;
  }

  get filterableMetricProperties(): MetricPropertyDefinition[] {
    if (this.args.datasetId === 'no_dataset') {
      let groupedCDAs = groupBy(
        this.reportingMetrics.getFilterableDatasetCdaAttributesFor('conversation'),
        (filter) => filter.group!,
      );

      return flatten([
        this.reportingMetrics.getPropertiesByIds(this.args.filterableProperties),
        groupedCDAs[CUSTOM_COMPANY_ATTRIBUTE],
        groupedCDAs[CUSTOM_USER_ATTRIBUTE],
      ]).compact();
    } else {
      return flatten([
        this.reportingMetrics.getPropertiesByIds(this.args.filterableProperties),
        this.reportingMetrics.getFilterableDatasetCdaAttributesFor(this.args.datasetId),
      ]);
    }
  }

  get timezone() {
    if (this.args.renderTimezones) {
      return localStorage.getItem(`${this.reportId}_timezone`) || this.report?.reportTimezone;
    }
    return this.appService.app.timezone;
  }

  get defaultTimezone() {
    return localStorage.getItem(`${this.reportId}_timezone`) || this.appService.app.timezone;
  }

  @action
  updateFilter(
    index: any,
    attribute: { id: string; field: string },
    filterValues: any[],
    operator: string,
  ) {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'filtered',
      object: 'in_context_report',
      reportId: this.reportId,
      filter_name: attribute.id,
    });

    if (attribute.id === 'time') {
      if (this.args.useSharedDateRange) {
        this.reportingService.updateRange(filterValues);
      } else {
        // Update local report date range and show warnings
        this.report.applyDateRange(filterValues);
        this.reportingService.notifyDateRangeWarnings(filterValues);
      }
      return;
    }

    this.report.applyFilter(index, attribute, filterValues, operator);
  }

  @action
  openReportingAgent() {
    this.reportingAgentSidebar.openWithReport(this.report, this.reportState);
  }

  @action
  clearFilters() {
    this.report.rollbackAttributes(); // reset the date range to the default
    this.report.filters = {};
  }

  @action
  applyLogicalFilterOperator(operator: LogicalFilterOperator) {
    this.report.applyLogicalFilterOperator(operator);
  }

  get range() {
    let { rangeStart, rangeEnd } = this.queryParams;
    if (rangeStart && rangeEnd) {
      return Range.createFromParams(rangeStart, rangeEnd, this.timezone);
    }

    if (this.args.useSharedDateRange) {
      let reportingRange = this.reportingService.range as Range | null;
      if (reportingRange) {
        return reportingRange;
      }
    }

    return this.report.range;
  }

  get oldKeyForFilterStorage() {
    return `filters_for_${this.reportId}`;
  }

  get keyForFilterStorage() {
    return `reporting:${this.appService.app.id}:filters:${this.reportId}`;
  }

  getFiltersFromLocalStorage() {
    let key = this.keyForFilterStorage;
    let value = localStorage.getItem(key);
    if (value) {
      return JSON.parse(value);
    }
  }

  safelyGetFiltersFromLocalStorage() {
    try {
      return this.getFiltersFromLocalStorage();
    } catch (error) {
      console.error(error);
      captureException(error);
    }
  }

  saveFiltersToLocalStorage(filters: TopLevelFilter) {
    let key = this.keyForFilterStorage;
    if (filters) {
      localStorage.setItem(key, JSON.stringify(filters));
    } else {
      localStorage.removeItem(key);
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::InContext::StaticReport': typeof StaticReport;
    'reporting/in-context/static-report': typeof StaticReport;
  }
}
